import { Shield, Pencil, PencilOff, Coins } from "lucide-react";
import { User } from '@supabase/supabase-js';
import therappiLogo from '@/assets/therappi-text-logo.svg';
import { DesktopNav } from '@/components/DesktopNav';
import { MobileNav } from '@/components/MobileNav';
import { useEditableContent } from '@/hooks/useEditableContent';
import { logoConfig } from '@/config/logoSizes';
import { LogoEditor } from '@/components/LogoEditor';
import { useLogoEditor } from '@/contexts/LogoEditorContext';
import { EditModeToggle } from '@/components/EditModeToggle';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { THERAPPI_DEMO_EMAIL } from '@/lib/utils';

interface AppHeaderProps {
  user: User | null;
  onSignOut: () => void;
}

export const AppHeader = ({ user, onSignOut }: AppHeaderProps) => {
  const safeConfidentialText = useEditableContent('header.safe_confidential', 'Safe & Confidential', 'ui', 'Safe and confidential badge text');
  const { isEditing, setIsEditing } = useLogoEditor();
  const { totalCredits } = useSubscription();
  const navigate = useNavigate();
  const [isDemoUser, setIsDemoUser] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsDemoUser(user?.email === THERAPPI_DEMO_EMAIL);
    };
    checkUser();
  }, [user]);
  
  // Editable spacing values (in pixels)
  const mobilePaddingY = useEditableContent('header.mobile_padding_y', '16', 'ui', 'Mobile header vertical padding (in pixels)');
  const desktopPaddingTop = useEditableContent('header.desktop_padding_top', '32', 'ui', 'Desktop header top padding (in pixels)');
  const desktopLogoToNavGap = useEditableContent('header.desktop_logo_to_nav_gap', '40', 'ui', 'Desktop spacing between logo and navigation (in pixels)');

  return (
    <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-50 relative">
      {/* Mobile Header Layout */}
      <div className="md:hidden">
        {/* Top row: Safe badge (left) and Credits (right) */}
        <div className="flex items-center justify-between px-4 pt-2 pb-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Shield className="h-3.5 w-3.5" />
            <span className="font-light">{safeConfidentialText}</span>
          </div>
          {user && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/credits')}
              className="gap-1.5 bg-primary/5 hover:bg-primary/10 h-7 px-2"
            >
              <Coins className="h-3.5 w-3.5 text-primary" />
              <span className="font-medium text-foreground text-xs">{totalCredits}</span>
            </Button>
          )}
        </div>
        {/* Bottom row: Menu (left), Logo (center) */}
        <div 
          className="flex items-center justify-between gap-4 px-4"
          style={{ paddingTop: '4px', paddingBottom: `${mobilePaddingY}px` }}
        >
          <div className="flex items-center w-12">
            <MobileNav user={user} onSignOut={onSignOut} />
          </div>
          <div className="flex items-center justify-center flex-1">
            <img
              src={therappiLogo}
              alt="Therappi®"
              className={`${logoConfig.header.mobile} object-contain cursor-pointer`}
              onClick={() => navigate('/today')}
            />
          </div>
          <div className="w-12" /> {/* Spacer for centering */}
        </div>
      </div>

      {/* Desktop Header Layout */}
      <div className="hidden md:block">
        {/* Safe Badge, Credits Counter and Edit Buttons - absolute top right corner */}
        <div className="absolute top-3 right-4 sm:right-6 flex items-center gap-3 text-sm text-muted-foreground z-[60] pointer-events-auto">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span className="font-light">{safeConfidentialText}</span>
          </div>
          {user && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/credits')}
              className="gap-2 bg-primary/5 hover:bg-primary/10"
            >
              <Coins className="h-4 w-4 text-primary" />
              <span className="font-medium text-foreground">{totalCredits}</span>
            </Button>
          )}
          {!isDemoUser && (
            <div className="flex items-center gap-2">
              <EditModeToggle />
              <Button
                variant={isEditing ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIsEditing(!isEditing)}
                className="gap-2"
              >
                <Pencil className="h-4 w-4" />
                <span>{isEditing ? 'Exit Logo Edit' : 'Edit Logos'}</span>
              </Button>
            </div>
          )}
        </div>

        <div className="container mx-auto px-4 sm:px-6">
          {/* Desktop Layout - Logo Top, Nav Bottom */}
          <div 
            className="flex flex-col"
            style={{ paddingTop: `${desktopPaddingTop}px`, paddingBottom: '4px' }}
          >
            <div className="flex items-end">
              <LogoEditor logoId="header-desktop-therappi" className={`${logoConfig.header.desktop}`} isEditing={isEditing}>
                <img
                  src={therappiLogo}
                  alt="Therappi®"
                  className="w-full h-full object-contain cursor-pointer"
                  onClick={() => navigate('/today')}
                />
              </LogoEditor>
            </div>
            <div 
              className="flex items-center justify-end gap-2 relative z-[70]"
              style={{ marginTop: `${desktopLogoToNavGap}px` }}
            >
              <DesktopNav user={user} onSignOut={onSignOut} />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
